// RowDetails.tsx
import { MainTableItem } from '../../../contexts/apiService';

interface RowDetailsProps {
    item: MainTableItem;
}

interface DetailField {
    label: string;
    key: keyof MainTableItem;
    formatter?: (value: any) => string;
    className?: string;
}

interface DetailSection {
    title?: string;
    fields: DetailField[];
    className?: string;
}

const RowDetails = ({ item }: RowDetailsProps) => {
    // Helper function to format numbers consistently
    const formatNumber = (value: any): string => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Helper function to format currency
    const formatCurrency = (value: any): string => {
        return `$${formatNumber(value)}`;
    };

    // Helper function to format status with conditional styling
    const formatStatus = (value: any): string => {
        return value || '';
    };

    // Configuration for detail sections
    const detailSections: DetailSection[] = [
        {
            fields: [
                { label: "Gross Barrels", key: "gross_barrels", formatter: formatNumber },
                { label: "BS&W", key: "bsw", formatter: formatNumber },
                { label: "Observed Temp.", key: "observed_temperature", formatter: formatNumber },
                { label: "Observed Gravity", key: "observed_gravity", formatter: formatNumber },
                { label: "Corrected Gravity", key: "corrected_gravity", formatter: formatNumber },
            ]
        },
        {
            fields: [
                { label: "Carrier", key: "carrier" },
                { label: "Status", key: "status", formatter: formatStatus, className: "status-field" },
                { label: "Station Owner", key: "station_owner" },
                { label: "Station", key: "destination" },
                { label: "Market", key: "market" },
            ]
        },
        {
            fields: [
                { label: "Total Trucking", key: "trucking", formatter: formatCurrency },
                { label: "Purchase Deduct", key: "purchase_deduct", formatter: formatCurrency },
                { label: "Purchase BS&W Deduct", key: "purchase_bsw_deduct", formatter: formatCurrency },
                { label: "Additional Deduct", key: "additional_deduct", formatter: formatCurrency },
                { label: "Purchase Price", key: "purchase_price", formatter: formatCurrency },
            ]
        },
        {
            fields: [
                { label: "Sale Deduct", key: "sale_deduct", formatter: formatCurrency },
                { label: "Sale BS&W Deduct", key: "sale_bsw_deduct", formatter: formatCurrency },
                { label: "Sale Additional Deduct", key: "sale_location_deduct", formatter: formatCurrency },
                { label: "Station Charge", key: "station_charge", formatter: formatCurrency },
                { label: "Sale Price", key: "sale_price", formatter: formatCurrency },
            ]
        },
        {
            fields: [
                { label: "Total Purchase", key: "total_purchase", formatter: formatCurrency },
                { label: "Total Sale", key: "total_sale", formatter: formatCurrency },
                { label: "Net Profit", key: "net_profit", formatter: formatCurrency },
            ]
        }
    ];

    // Component to render individual field
    const DetailField = ({ field }: { field: DetailField }) => {
        const value = item[field.key];
        const formattedValue = field.formatter ? field.formatter(value) : (value || '');
        
        // Special handling for status field
        const getStatusClassName = () => {
            if (field.key === 'status' && value) {
                const statusLower = value.toString().toLowerCase();
                if (statusLower === 'rejected') {
                    return 'status-text-rejected';
                } else if (statusLower === 'completed') {
                    return 'status-text-completed';
                }
            }
            return '';
        };

        return (
            <div className="mtdSummary-rowDetails-rowItem">
                <div className="mtdSummary-rowDetails-rowItemLabel">{field.label}</div>
                <div className={`${getStatusClassName()}`}>
                    {formattedValue}
                </div>
            </div>
        );
    };

    // Component to render section
    const DetailSection = ({ section }: { section: DetailSection }) => (
        <div className={"mtdSummary-rowDetails-Section"}>
            <div>
                {section.fields.map((field) => (
                    <DetailField key={field.key} field={field}/>
                ))}
            </div>
        </div>
    );

    return (
        <div className="mtdSummary-rowDetails-mainContainer">
            {detailSections.map((section, index) => (
                <DetailSection key={index} section={section} />
            ))}
        </div>
    );
};

export default RowDetails;